import { t } from 'i18next';
import { capitalize, lowerFirst } from 'lodash';
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { ApiStatus, NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { useNotificationContext, useSelectionCountContext } from 'core/states';
import { GetManyResponse, GetManyResult, GetOneResult } from 'core/types';
import { FilenetStatus, QuoteEntityType, UpsertQuoteAction } from 'quotes/enums';
import { quoteService } from 'quotes/services';
import {
  IAddFinderPartsToNewQuoteData,
  IAddFinderPartsToQuoteData,
  IAttachedVendorQuote,
  IBaseQuote,
  ICustomerQuote,
  ICustomerQuoteListByCustomerPayload,
  IProspectContactInfoResponse,
  IQuoteActionOptions,
  IQuoteAttachedFilesResponse,
  IQuoteExtraData,
  IQuoteFile,
  IVendorQuote,
  IVendorQuoteListRequestPayload,
} from 'quotes/types';

const FILENET_SENDING_POLL_INTERVAL = 15000;
// Past the BE's 15-min stale-claim window a stuck 'sending' quote needs a manual retry click
// anyway, so poll a little beyond it and stop rather than run forever on a parked tab.
const FILENET_SENDING_POLL_MAX_MS = 20 * 60 * 1000;

export const useGetQuote = (
  criteria: { quoteId: string; isOrder?: number },
  enabled = true
): GetOneResult<IBaseQuote, IQuoteExtraData> => {
  const sendingPollStartedAtRef = useRef<number | null>(null);

  const { data, isLoading, isFetching, isError } = useQuery(
    ['quote', criteria],
    ({ signal }) => quoteService.getQuote(criteria, signal),
    {
      keepPreviousData: false,
      enabled,
      // Poll while a send is in flight — the BE never pushes the result. Terminal statuses stop
      // the interval (and reset the ceiling); non-vendor quotes have no filenetStatus and never
      // start it.
      refetchInterval: (data) => {
        if (FilenetStatus.SENDING !== (data?.record as IVendorQuote | undefined)?.filenetStatus) {
          sendingPollStartedAtRef.current = null;

          return false;
        }

        if (null === sendingPollStartedAtRef.current) {
          sendingPollStartedAtRef.current = Date.now();
        }

        return Date.now() - sendingPollStartedAtRef.current < FILENET_SENDING_POLL_MAX_MS
          ? FILENET_SENDING_POLL_INTERVAL
          : false;
      },
    }
  );

  return {
    record: data?.record ?? null,
    extraData: data?.extraData ?? null,
    isLoading,
    isFetching,
    isError,
  };
};

export const useGetCustomerQuoteByCustomer = (
  requestPayload: ICustomerQuoteListByCustomerPayload,
  { enabled = true }: { enabled: boolean }
): GetOneResult<ICustomerQuote> => {
  const { showLoadingNotification, showSuccessNotification } = useNotificationContext();

  const { data, isLoading, isFetching, isError } = useQuery(
    ['customerQuoteByCustomer', requestPayload],
    ({ signal }) => quoteService.getCustomerQuoteByCustomer(requestPayload, signal),
    { enabled }
  );

  const record = data?.record ?? null;

  useEffect(() => {
    if (isLoading) {
      showLoadingNotification({ message: t('messageSearchingQuote') }, NotificationPosition.FORM);
    }
  }, [isLoading, showLoadingNotification]);

  useEffect(() => {
    if (record) {
      showSuccessNotification({ message: t('messageQuoteFound') }, NotificationPosition.FORM);
    }
  }, [record, showSuccessNotification]);

  return {
    record,
    isLoading,
    isFetching,
    isError,
  };
};

export const useGetVendorQuoteByVendor = (
  requestPayload,
  { enabled = true }: { enabled: boolean }
): GetOneResult<IVendorQuote> => {
  const { showLoadingNotification, showSuccessNotification } = useNotificationContext();

  const { data, isLoading, isFetching, isError } = useQuery(
    ['vendorQuoteByVendor', requestPayload],
    ({ signal }) => quoteService.getVendorQuoteByVendor(requestPayload, signal),
    { enabled }
  );

  const record = data?.record ?? null;

  useEffect(() => {
    if (isLoading) {
      showLoadingNotification({ message: t('messageSearchingQuote') }, NotificationPosition.FORM);
    }
  }, [isLoading, showLoadingNotification]);

  useEffect(() => {
    if (record) {
      showSuccessNotification({ message: t('messageQuoteFound') }, NotificationPosition.FORM);
    }
  }, [record, showSuccessNotification]);

  return {
    record: record,
    isLoading,
    isFetching,
    isError,
  };
};

export const useGetVendorQuotes = (
  requestPayload: IVendorQuoteListRequestPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<IVendorQuote>(
    quoteService.getVendorQuotes,
    'vendorQuotes',
    requestPayload,
    options
  );

export const useGetAttachedVendorQuotes = (
  requestPayload: IVendorQuoteListRequestPayload,
  options: { enabled?: boolean; fetchAllPages?: boolean } = {}
) =>
  useInfiniteQuery<IAttachedVendorQuote>(
    quoteService.getAttachedVendorQuotes,
    'attachedVendorQuotes',
    requestPayload,
    options
  );

export const useGetQuoteFiles = (
  requestPayload: any,
  options: { enabled?: boolean; onSuccess?: (data: GetManyResponse<IQuoteFile>) => void } = {}
): Omit<GetManyResult<IQuoteAttachedFilesResponse>, 'records'> & {
  records: IQuoteAttachedFilesResponse;
} => {
  const { data, isLoading, isError, error } = useQuery(
    ['quoteFiles', requestPayload],
    ({ signal }) => quoteService.getQuoteFiles(requestPayload, signal),
    {
      keepPreviousData: false,
      ...options,
    }
  );

  return {
    records: (data?.records as unknown as IQuoteAttachedFilesResponse) ?? {
      bomImport: [],
      quoteDownload: [],
      spaSent: [],
      upload: [],
      vendorQuoteImport: [],
    },
    count: data?.count ?? 0,
    isLoading,
    isError,
    statusCode: (error as any)?.response?.status ?? null,
  };
};

// isError is part of the contract: a failed lookup means UNKNOWN, not "no match" — collapsing
// the two would silently skip the duplicate-contact check.
export const useGetProspectContactInfo = (
  email: string,
  enabled: boolean
): {
  contactInfo: IProspectContactInfoResponse | null;
  isError: boolean;
  isFetching: boolean;
} => {
  // retry: false is deliberate — this fires per debounced keystroke, so a retry would
  // outlive the email it was fired for. Editing the email re-keys the query; that is the retry.
  const { data, isError, isFetching } = useQuery(
    ['prospectContactInfo', email],
    ({ signal }) => quoteService.getProspectContactInfo({ email }, signal),
    { enabled, keepPreviousData: false, retry: false }
  );

  return {
    contactInfo: data ?? null,
    isError,
    isFetching,
  };
};

export const useCreateProspectQuote = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageCreateProspectQuoteSuccess') },
    loading: { message: t('messageCreateProspectQuoteInProgress') },
    error: { title: t('errorTitleCreateProspectQuote') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(quoteService.createProspectQuote, { ...errors });

  return { createProspectQuote: mutateAsync };
};

export const useSaveVendorQuoteMainInfo = (position = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageVendorQuoteDetailsUpdateSuccess') },
    loading: { message: t('messageVendorQuoteDetailsUpdateInProgress') },
    error: { title: t('errorTitleVendorQuoteDetailsUpdate') },
    position,
    // The VQ record is a prop with no setter (from `useGetQuote`): refetch rather than merge.
    invalidateQueries: ['quote'],
  });

  const { mutateAsync, isLoading } = useMutation(quoteService.saveVendorQuoteMainInfo, {
    ...errors,
  });

  return { saveVendorQuoteMainInfo: mutateAsync, isSaving: isLoading };
};

export const useQuoteActions = ({
  notificationPosition,
  entityType = QuoteEntityType.QUOTE,
  action,
}: IQuoteActionOptions = {}) => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useExportQuote(),
    ...useExportBulkVendorQuoteSkuParts(),
    ...useSendVendorQuoteAsSpa(),
    ...useSendBulkVendorQuoteAsSpa(),
    ...useUpsertCustomerQuote(entityType),
    ...useUpsertVendorQuote(entityType),
    ...useUpsertCustomerQuoteAddress(),
    ...useAttachQuoteToQuote(selectionCount),
    ...useAttachQuoteToProject(),
    ...useDetachVendorQuote(),
    ...useReattachVendorQuote(),
    ...useUpsertQuoteNote(notificationPosition, entityType),
    ...useAddPartsToCustomerQuote(selectionCount),
    ...useAddPartsToVendorQuote(selectionCount),
    ...useAddBomPartsToCustomerQuote(selectionCount),
    ...useAddBomPartsToVendorQuote(selectionCount),
    ...useAddFinderPartsToCustomerQuote(selectionCount),
    ...useUpsertQuoteVersionData(entityType),
    ...useSubmitCustomerOrder(),
    ...useSubmitVendorOrder(),
    ...useSendVendorQuoteToVendor(),
    ...useSendVendorQuoteToFilenet(),
    ...useCreateCustomerOrderFromQuote(),
    ...useCreateVendorOrderFromQuote(),
    ...useAttachQuoteFile(entityType),
    ...useDetachQuoteFile(entityType),
    ...useUpsertCustomerQuotes(entityType, action, selectionCount, notificationPosition),
    ...useUpsertVendorQuotes(entityType, action, selectionCount, notificationPosition),
  };
};

const useDetachVendorQuote = () => {
  const { selectionCount } = useSelectionCountContext();

  const { errors } = useHandleErrors({
    success: {
      message: t('messageDetachVendorQuoteSuccess', {
        count: selectionCount,
      }),
    },
    loading: {
      message: t('messageDetachVendorQuoteInProgress', {
        count: selectionCount,
      }),
    },
    error: {
      title: t('errorTitleDetachVendorQuote', {
        count: selectionCount,
      }),
    },
    invalidateQueries: ['attachedVendorQuotes'],
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(quoteService.detachVendorQuote, {
    ...errors,
  });

  return { detachVendorQuote: mutateAsync };
};

const useReattachVendorQuote = () => {
  const { selectionCount } = useSelectionCountContext();

  const { errors } = useHandleErrors({
    success: {
      message: t('messageReattachVendorQuoteSuccess', {
        count: selectionCount,
      }),
    },
    loading: {
      message: t('messageReattachVendorQuoteInProgress', {
        count: selectionCount,
      }),
    },
    error: {
      title: t('errorTitleReattachVendorQuote', {
        count: selectionCount,
      }),
    },
    invalidateQueries: ['attachedVendorQuotes'],
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(quoteService.reattachVendorQuote, {
    ...errors,
  });

  return { reattachVendorQuote: mutateAsync };
};

const useExportQuote = () => {
  const { mutateAsync } = useMutation(quoteService.exportQuote);

  const exportQuote = (data) => mutateAsync(data);

  return { exportQuote };
};

const useExportBulkVendorQuoteSkuParts = () => {
  const { mutateAsync } = useMutation(quoteService.exportBulkVendorQuoteSkuParts);

  const exportBulkVendorQuoteSkuParts = (
    data: Parameters<typeof quoteService.exportBulkVendorQuoteSkuParts>[0]
  ) => mutateAsync(data);

  return { exportBulkVendorQuoteSkuParts };
};

// Notifications + polling are driven by the caller via useCliJobHandler (drawer/list positioned),
// so these stay thin mutation wrappers like useExportQuote.
const useSendVendorQuoteAsSpa = () => {
  const { mutateAsync } = useMutation(quoteService.sendVendorQuoteAsSpa);

  const sendVendorQuoteAsSpa = (data: Parameters<typeof quoteService.sendVendorQuoteAsSpa>[0]) =>
    mutateAsync(data);

  return { sendVendorQuoteAsSpa };
};

const useSendBulkVendorQuoteAsSpa = () => {
  const { mutateAsync } = useMutation(quoteService.sendBulkVendorQuoteAsSpa);

  const sendBulkVendorQuoteAsSpa = (
    data: Parameters<typeof quoteService.sendBulkVendorQuoteAsSpa>[0]
  ) => mutateAsync(data);

  return { sendBulkVendorQuoteAsSpa };
};

const usePrivateHandleErrorsToUpsertQuote = (entityType = QuoteEntityType.QUOTE) =>
  useHandleErrors({
    success: ({ quote }: { quote: { id: number } }) => {
      return {
        message: quote?.id
          ? t('messageQuoteUpdateSuccess', { entityType })
          : t('messageQuoteCreateSuccess', { entityType }),
      };
    },
    loading: ({ quote }: { quote: { id: number } }) => {
      return {
        message: quote?.id
          ? t('messageQuoteUpdateInProgress', { entityType })
          : t('messageQuoteCreateInProgress', { entityType }),
      };
    },
    error: ({ quote }: { quote: { id: number } }) => {
      return {
        title: quote?.id
          ? t('errorTitleQuoteUpdate', { entityType: lowerFirst(entityType) })
          : t('errorTitleQuoteCreate', { entityType: lowerFirst(entityType) }),
      };
    },
    position: NotificationPosition.FORM,
  });

const useCustomHandleErrorsToUpsertQuote = (
  entityType = QuoteEntityType.QUOTE,
  action = UpsertQuoteAction.CREATE,
  count: number,
  notificationPosition = NotificationPosition.TOP
) =>
  useHandleErrors({
    success: {
      message: t(`messageQuotes${capitalize(action)}Success`, { entityType, count }),
    },
    loading: {
      message: t(`messageQuotes${capitalize(action)}InProgress`, {
        entityType: lowerFirst(entityType),
        count,
      }),
    },
    error: {
      title: t(`errorTitleQuotes${capitalize(action)}`, {
        entityType: lowerFirst(entityType),
        count,
      }),
    },
    position: notificationPosition,
  });

const useUpsertCustomerQuote = (entityType = QuoteEntityType.QUOTE) => {
  const { errors } = usePrivateHandleErrorsToUpsertQuote(entityType);

  const { mutateAsync } = useMutation(quoteService.upsertCustomerQuote, {
    ...errors,
  });

  return { upsertCustomerQuote: mutateAsync };
};

const useUpsertCustomerQuotes = (
  entityType = QuoteEntityType.QUOTE,
  action: UpsertQuoteAction,
  count: number,
  notificationPosition: NotificationPosition
) => {
  const { errors } = useCustomHandleErrorsToUpsertQuote(
    entityType,
    action,
    count,
    notificationPosition
  );

  const { mutateAsync } = useMutation(quoteService.upsertCustomerQuotes, {
    ...errors,
  });

  return { upsertCustomerQuotes: mutateAsync };
};

const useUpsertVendorQuotes = (
  entityType = QuoteEntityType.QUOTE,
  action: UpsertQuoteAction,
  count: number,
  notificationPosition: NotificationPosition
) => {
  const { errors } = useCustomHandleErrorsToUpsertQuote(
    entityType,
    action,
    count,
    notificationPosition
  );

  const { mutateAsync } = useMutation(quoteService.upsertVendorQuotes, {
    ...errors,
  });

  return { upsertVendorQuotes: mutateAsync };
};

const useUpsertVendorQuote = (entityType = QuoteEntityType.QUOTE) => {
  const { errors } = usePrivateHandleErrorsToUpsertQuote(entityType);

  const { mutateAsync } = useMutation(quoteService.upsertVendorQuote, {
    ...errors,
  });

  return { upsertVendorQuote: mutateAsync };
};

const useUpsertCustomerQuoteAddress = () => {
  const { errors } = useHandleErrors({
    success: ({ address }: { address: { id: number } }) => {
      return {
        message: address?.id ? t('messageAddressUpdateSuccess') : t('messageAddressCreateSuccess'),
      };
    },
    loading: ({ address }: { address: { id: number } }) => {
      return {
        message: address?.id
          ? t('messageAddressUpdateInProgress')
          : t('messageAddressCreateInProgress'),
      };
    },
    error: ({ address }: { address: { id: number } }) => {
      return {
        title: address?.id ? t('errorTitleAddressUpdate') : t('errorTitleAddressCreate'),
      };
    },
    position: NotificationPosition.FORM,
    // The inactive project link flags are computed by /quote/details only.
    invalidateQueries: ['quote'],
  });

  const { mutateAsync } = useMutation(quoteService.upsertCustomerQuoteAddress, {
    ...errors,
  });

  return { upsertCustomerQuoteAddress: mutateAsync };
};

const useAttachQuoteToQuote = (selectionCount) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageQuoteToQuoteAttachSuccess', {
        count: selectionCount || 1,
      }),
    },
    loading: {
      message: t('messageQuoteToQuoteAttachInProgress', {
        count: selectionCount || 1,
      }),
    },
    error: {
      title: t('errorTitleQuoteToQuoteAttach', {
        count: selectionCount || 1,
      }),
    },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(quoteService.upsertQuoteToQuoteAssociation, {
    ...errors,
  });

  const attachQuoteToQuote = (data) => mutateAsync(data);

  return { attachQuoteToQuote };
};

const useAttachQuoteToProject = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageQuoteToProjectAttachSuccess') },
    loading: { message: t('messageQuoteToProjectAttachInProgress') },
    error: { title: t('errorTitleQuoteToProjectAttach') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(quoteService.upsertQuoteToProjectAssociation, {
    ...errors,
  });

  const attachQuoteToProject = (data) => mutateAsync(data);

  return { attachQuoteToProject };
};

const useUpsertQuoteNote = (
  position = NotificationPosition.FORM,
  entityType = QuoteEntityType.QUOTE
) => {
  const { errors } = useHandleErrors({
    success: ({ quoteNote }: { quoteNote: { id: number } }) => {
      return {
        message: quoteNote?.id
          ? t('messageQuoteNoteUpdateSuccess', { entityType })
          : t('messageQuoteNoteCreateSuccess', { entityType }),
      };
    },
    loading: ({ quoteNote }: { quoteNote: { id: number } }) => {
      return {
        message: quoteNote?.id
          ? t('messageQuoteNoteUpdateInProgress', { entityType: lowerFirst(entityType) })
          : t('messageQuoteNoteCreateInProgress', { entityType: lowerFirst(entityType) }),
      };
    },
    error: ({ quoteNote }: { quoteNote: { id: number } }) => {
      return {
        title: quoteNote?.id
          ? t('errorTitleQuoteNoteUpdate', { entityType: lowerFirst(entityType) })
          : t('errorTitleQuoteNoteCreate', { entityType: lowerFirst(entityType) }),
      };
    },
    position,
  });

  const { mutateAsync } = useMutation(quoteService.upsertQuoteNote, {
    ...errors,
  });

  const upsertQuoteNote = (data) => mutateAsync(data);

  return { upsertQuoteNote };
};

const useAddPartsToCustomerQuote = (count: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorPartsAdding', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(quoteService.addPartsToCustomerQuote, {
    ...errors,
  });

  return { addPartsToCustomerQuote: mutateAsync };
};

const useAddPartsToVendorQuote = (count: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorPartsAdding', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(quoteService.addPartsToVendorQuote, {
    ...errors,
  });

  return { addPartsToVendorQuote: mutateAsync };
};

const useAddBomPartsToCustomerQuote = (count: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorPartsAdding', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    async (data) => {
      if (data.targetQuoteId) {
        return quoteService.addBomPartsToCustomerQuote(data);
      }

      return quoteService.addBomPartsToNewCustomerQuote(data);
    },
    {
      ...errors,
    }
  );

  return { addBomPartsToCustomerQuote: mutateAsync };
};

const useAddBomPartsToVendorQuote = (count: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorPartsAdding', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    async (data) => {
      if (data.targetQuoteId) {
        return quoteService.addBomPartsToVendorQuote(data);
      }

      return quoteService.addBomPartsToNewVendorQuote(data);
    },
    {
      ...errors,
    }
  );

  return { addBomPartsToVendorQuote: mutateAsync };
};

const useAddFinderPartsToCustomerQuote = (count: number) => {
  const { errors } = useHandleErrors({
    error: { title: t('errorPartsAdding', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    async (data: IAddFinderPartsToQuoteData | IAddFinderPartsToNewQuoteData) => {
      if ('targetQuoteId' in data) {
        return quoteService.addFinderPartsToCustomerQuote(data);
      }

      return quoteService.addFinderPartsToNewCustomerQuote(data);
    },
    {
      ...errors,
    }
  );

  return { addFinderPartsToCustomerQuote: mutateAsync };
};

const useUpsertQuoteVersionData = (entityType = QuoteEntityType.QUOTE) => {
  const { errors } = useHandleErrors({
    success: ({ quoteVersionData }: { quoteVersionData: { id: number } }) => {
      return {
        message: quoteVersionData?.id
          ? t('messageQuoteVersionDataUpdateSuccess', { entityType })
          : t('messageQuoteVersionDataCreateSuccess', { entityType }),
      };
    },
    loading: ({ quoteVersionData }: { quoteVersionData: { id: number } }) => {
      return {
        message: quoteVersionData?.id
          ? t('messageQuoteVersionDataUpdateInProgress', { entityType: lowerFirst(entityType) })
          : t('messageQuoteVersionDataCreateInProgress', { entityType: lowerFirst(entityType) }),
      };
    },
    error: ({ quoteVersionData }: { quoteVersionData: { id: number } }) => {
      return {
        title: quoteVersionData?.id
          ? t('errorTitleQuoteVersionDataUpdate', { entityType: lowerFirst(entityType) })
          : t('errorTitleQuoteVersionDataCreate', { entityType: lowerFirst(entityType) }),
      };
    },
    position: NotificationPosition.FORM,
    invalidateQueries: ['quoteVersionData', 'customerQuoteParts', 'vendorQuoteParts'],
  });

  const { mutateAsync } = useMutation(quoteService.upsertQuoteVersionData, {
    ...errors,
  });

  return { upsertQuoteVersionData: mutateAsync };
};

const useSubmitCustomerOrder = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleQuoteSubmitOrder') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync, isLoading } = useMutation(quoteService.submitCustomerOrder, {
    ...errors,
  });

  return { submitCustomerOrder: mutateAsync, isSubmitCustomerOrderLoading: isLoading };
};

const useSubmitVendorOrder = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageOrderSubmitSuccess') },
    loading: { message: t('messageOrderSubmitInProgress') },
    error: { title: t('errorTitleQuoteSubmitOrder') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['quote', 'vendorQuoteParts'],
  });

  const { mutateAsync, isLoading } = useMutation(quoteService.submitVendorOrder, {
    ...errors,
  });

  return { submitVendorOrder: mutateAsync, isSubmitVendorOrderLoading: isLoading };
};

const useSendVendorQuoteToVendor = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageQuoteSendToVendorSuccess') },
    loading: { message: t('messageQuoteSendToVendorInProgress') },
    error: { title: t('errorTitleQuoteSendToVendor') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(quoteService.sendVendorQuoteToVendor, {
    ...errors,
  });

  return { sendVendorQuoteToVendor: mutateAsync, isSendVendorQuoteToVendorLoading: isLoading };
};

const useSendVendorQuoteToFilenet = () => {
  const queryClient = useQueryClient();

  const { showNotification, showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const { errors } = useHandleErrors({
    loading: { message: t('messageQuoteSendToFileNetInProgress') },
    error: { title: t('errorTitleQuoteSendToFileNet') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(quoteService.sendVendorQuoteToFilenet, {
    ...errors,
    // Overrides the generic handler on purpose: `success` is false for BOTH a failed send and a
    // claim miss, so only `filenetStatus` can tell them apart.
    onSuccess: ({ filenetStatus, filenetError, filenetMessage }) => {
      if (FilenetStatus.SENT === filenetStatus) {
        showSuccessNotification(
          { message: t('messageQuoteSendToFileNetSuccess') },
          NotificationPosition.TOP
        );
      } else if (FilenetStatus.SENDING === filenetStatus) {
        // Claim miss — another send is already running, nothing was re-uploaded.
        showNotification({
          title: t('titleInfo'),
          message: filenetMessage || t('messageQuoteSendToFileNetAlreadyInProgress'),
          type: ApiStatus.INFO,
          position: NotificationPosition.TOP,
        });
      } else {
        showErrorNotification(
          { title: t('errorTitleQuoteSendToFileNet'), message: filenetError ?? undefined },
          NotificationPosition.TOP
        );
      }

      // Returned so the mutation stays pending until the quote refetches — the button then
      // reflects server truth, not the pre-send status.
      return queryClient.invalidateQueries('quote');
    },
  });

  return { sendVendorQuoteToFilenet: mutateAsync, isSendVendorQuoteToFilenetLoading: isLoading };
};

const useCreateCustomerOrderFromQuote = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleOrderCreate') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quoteService.createCustomerOrderFromQuote(data), {
    ...errors,
  });

  return { createCustomerOrderFromQuote: mutateAsync };
};

const useCreateVendorOrderFromQuote = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleOrderCreate') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => quoteService.createVendorOrderFromQuote(data), {
    ...errors,
  });

  return { createVendorOrderFromQuote: mutateAsync };
};

const useAttachQuoteFile = (entityType = QuoteEntityType.QUOTE) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageQuoteFileAttachInProgress', { entityType }) },
    success: { message: t('messageQuoteFileAttachSuccess', { entityType }) },
    error: { title: t('errorTitleQuoteFileAttach', { entityType }) },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['quoteFiles'],
  });

  const { mutateAsync } = useMutation(quoteService.attachQuoteFile, {
    ...errors,
  });

  return { attachQuoteFile: mutateAsync };
};

const useDetachQuoteFile = (entityType = QuoteEntityType.QUOTE) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageQuoteFileDetachSuccess', { entityType }) },
    loading: { message: t('messageQuoteFileDetachInProgress', { entityType }) },
    error: { title: t('errorTitleQuoteFileDetach', { entityType }) },
    position: NotificationPosition.DRAWER,
    invalidateQueries: ['quoteFiles'],
  });

  const { mutateAsync } = useMutation(quoteService.detachQuoteFile, {
    ...errors,
  });

  return { detachQuoteFile: mutateAsync };
};
